<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="sequence-share-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="sequence-share-form" @submit.prevent="onSubmit">
        <div class="sequence-share-form-body">
          <v-toolbar
            flat
            height="76"
          >
            <v-toolbar-title class="sequence-share-toolbar-title">Import shared Routines</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn 
              @click="toSmartSequences"
              color="accent"
              elevation="0"
              rounded
            >
              Browse all
            </v-btn>
            <v-btn
              color="accent"
              icon
              tile
              @click="closeDialog"
            >
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container class="new-practice-card-container">
            <v-text-field
              :value="searchValue"
              class="bh-input-pill rounded-pill"
              color="accent"
              height="48px"
              placeholder="Search for routines"
              filled
              clearable
              hide-details
              @input="onSearch('query', $event)"
              @click:clear="clearSearch"
              @click:append="runSearch"
              @keydown.enter="runSearch"
            >
              <template v-slot:append>
                <v-progress-circular
                  v-if="sequenceDefinitionsLoading"
                  size="16"
                  width="2"
                  color="info"
                  indeterminate
                ></v-progress-circular>
                <v-icon v-else>bhi-loupe</v-icon>
              </template>
            </v-text-field>
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <div class="sequence-share-patients">
              <main-user-table
                :headers="headers"
                :items="computedSequenceDefinitions"
                :items-per-page="queryParams.pageSize"
                :server-items-length="computedSequenceDefinitions.totalCount || -1"
                :page="queryParams.page"
                :row-component="'sequence-definition-table-row'"
                @choose-sequence="importSequence"
                @action:get-data="(data) => (getDataAction({ ...data}, false))"
              />
            </div>
          </v-container>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import MainUserTable from "@/components/tables/MainUserTable"
import RouterMixin from "@/mixins/router"
import { debounce } from '@/helpers/debounce'

export default {
  name: 'SequenceImportDialog',
  components: {
    MainUserTable,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    sequenceDefinitions: {
      type: Array,
      default: ()=>{[]}
    },
    sequenceDefinitionsLoading: {
      type: Boolean,
      default: false
    }
  },
  mixins: [RouterMixin],
  data () {
    return {
      dialog: false,
      headers: [],
      queryParams: {},
      searchValue: '',
    }
  },
  watch: {
    showDialog: function (val) {
      if (val) {
        this.dialog = true
        this.$emit('load-shared-sequences')
      }
    }
  },
  computed: {
    computedSequenceDefinitions() {
      return this.sequenceDefinitions
    }
  },
  methods: {
    closeDialog () {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === "Escape" && key.isTrusted) this.closeDialog()
    },
    clearForm () {
      this.$refs.form.reset()
    },
    getData(data = {}) {
    },
    searchRun(text) {
      debounce(()=>{
        this.getDataAction({ ...this.queryParams, query: text }, false)
      }, 500, 'getSequences')
    },
    importSequence(sequenceId) {
      debounce(()=>{
        this.$emit('import-sequence', sequenceId)
        this.closeDialog()
      },100, 'importSequence')
    },
    toSmartSequences() {
      this.$router.push({ name: "Smart Routines", path: "/import-routines/"})
    },
    clearSearch() {
      this.searchValue = ''
      this.$emit('search-clear')
    },
    runSearch() {
      if (this.searchValue)
        this.$emit('search-run', this.searchValue)
    },
    onSearch (key, value) {
      this.searchValue = value
      debounce(() => {
        this.$emit('search-on:input', { key, value })
      }, 1000, 'SequencesSearch')
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.sequence-share {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2*$gutter}) !important;
    // looks little hacky
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-weight: bold;
  }
  &-tabs {
    padding:0 24px;
    background:#ffffff;
    .v-tab {
      color:$bh-blue;
      text-transform: none;
      letter-spacing: 0;
      font-size: 1em;
      &:not(.v-tab--active) {
        color:white;
      }
    }
    .v-tabs-slider {
      background:$bh-blue;
    }
  }
  &-v-tabs-items {
    background:$bh-blue--4 !important;
  }

  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5*$gutter;
      padding-right: 1.5*$gutter;
    }
    &-container {
      padding-left: 1.5*$gutter;
      padding-right: 1.5*$gutter;
    }
    &-footer {
      padding: 1.5*$gutter;
    }
  }
  &-form {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;
    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5*$gutter;
        padding-right: 1.5*$gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25*$gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25*$gutter
    }
  }
  &-user-patients {
    padding: 12px 24px;
  }
}
</style>
